/**
 * Settings → Contract templates (C6): the block builder (ordered blocks, conditions, texts per
 * language, clauses by code) and the clause library. Contracts keep a frozen copy of the blocks.
 */

import {
  type Block,
  type BlockKindEnum,
  type Clause,
  type ContractTemplate,
  type ContractTemplateStatusEnum,
  useContractsClausesCreate,
  useContractsClausesList,
  useContractsClausesPartialUpdate,
  useContractsTemplatesCreate,
  useContractsTemplatesList,
  useContractsTemplatesPartialUpdate,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, GlassSheet, SelectField, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { asTexts, localized, type Texts } from "../components/TranslatedFields";
import { fieldErrors, useCanEditSettings } from "../lib/settings";
import { errorText, useRefreshContracts } from "./contracts/shared";

const KINDS: BlockKindEnum[] = ["title", "heading", "paragraph", "list", "notice", "clause", "parties", "services", "schedule", "signatures"];
const CONDITIONS = ["", "couple", "solo", "minor", "adult"] as const;
type Condition = (typeof CONDITIONS)[number];
const STATUSES: ContractTemplateStatusEnum[] = ["draft", "published", "archived"];
const LANGS = ["fr", "en"] as const;
const STATUS_TONE = { draft: "default", published: "success", archived: "danger" } as const;

/** Variables known to the builder (`builder.variables` in the API). */
const VARIABLES: Record<string, string[]> = {
  agency: ["trade_name", "legal_name", "legal_form", "rccm", "nui", "address", "phone", "website", "legal_rep", "legal_rep_title", "tax_center"],
  contract: ["number", "date"],
  package: ["name", "duration", "total", "total_words"],
  service: ["name"],
  client: ["full_name", "birth_date", "nationality", "id_number", "phone", "email", "address"],
  client2: ["full_name", "birth_date", "nationality", "id_number", "phone", "email", "address", "relation"],
  guardian: ["full_name", "birth_date", "nationality", "id_number", "phone", "email", "address", "relation"],
};

let counter = 0;
function newBlockId(kind: string): string {
  counter += 1;
  return `${kind}-${Date.now().toString(36)}${counter}`.slice(0, 60);
}

function VariablesHelp() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState("");
  return (
    <GlassCard as="aside" className="stack contract-variables">
      <h3>{t("contracts.builder.variablesTitle")}</h3>
      <p className="muted">{t("contracts.builder.variablesHelp")}</p>
      {Object.entries(VARIABLES).map(([group, names]) => (
        <div key={group} className="stack contract-variables__group">
          <strong>{t(`contracts.builder.groups.${group}`)}</strong>
          <div className="row">
            {names.map((name) => {
              const token = `{{ ${group}.${name} }}`;
              return (
                <button
                  key={name}
                  type="button"
                  className="code-chip contract-variable"
                  title={t("contracts.builder.copyVariable")}
                  onClick={() =>
                    void navigator.clipboard?.writeText(token).then(() => {
                      setCopied(token);
                      window.setTimeout(() => setCopied(""), 1500);
                    })
                  }
                >
                  {token}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {copied ? (
        <p role="status" className="ok">
          {t("contracts.builder.copied", { token: copied })}
        </p>
      ) : null}
      <p className="muted">{t("contracts.builder.noHardcoded")}</p>
    </GlassCard>
  );
}

function BlockEditor({
  block,
  index,
  count,
  clauses,
  onChange,
  onMove,
  onDelete,
}: {
  block: Block;
  index: number;
  count: number;
  clauses: Clause[];
  onChange: (block: Block) => void;
  onMove: (delta: number) => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const text = block.text ?? {};
  const items = block.items ?? [];
  const setItems = (next: Record<string, string>[]) => onChange({ ...block, items: next });
  return (
    <li className="installment-row stack contract-block">
      <div className="row between">
        <div className="row">
          <span className="code-chip">{index + 1}</span>
          <strong>{t(`contracts.builder.kinds.${block.kind}`)}</strong>
          {block.when ? <Badge tone="offer">{t(`contracts.builder.conditions.${block.when}`)}</Badge> : null}
        </div>
        <div className="row">
          <Button variant="ghost" aria-label={t("common.moveUp")} title={t("common.moveUp")} disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp size={16} aria-hidden="true" />
          </Button>
          <Button variant="ghost" aria-label={t("common.moveDown")} title={t("common.moveDown")} disabled={index === count - 1} onClick={() => onMove(1)}>
            <ArrowDown size={16} aria-hidden="true" />
          </Button>
          <Button variant="ghost" aria-label={t("contracts.builder.deleteBlock")} title={t("contracts.builder.deleteBlock")} onClick={onDelete}>
            <Trash2 size={16} aria-hidden="true" />
          </Button>
        </div>
      </div>
      <div className="form-grid">
        <SelectField
          label={t("contracts.builder.kind")}
          value={block.kind}
          options={KINDS.map((k) => ({ value: k, label: t(`contracts.builder.kinds.${k}`) }))}
          onChange={(e) => onChange({ ...block, kind: e.target.value as BlockKindEnum })}
        />
        <SelectField
          label={t("contracts.builder.condition")}
          value={block.when ?? ""}
          options={CONDITIONS.map((c) => ({ value: c, label: t(`contracts.builder.conditions.${c || "always"}`) }))}
          onChange={(e) => onChange({ ...block, when: e.target.value as Condition })}
        />
        {block.kind === "clause" ? (
          <SelectField
            label={t("contracts.builder.clause")}
            value={block.clause ?? ""}
            options={[
              { value: "", label: t("contracts.builder.pickClause") },
              ...clauses.map((c) => ({ value: c.code, label: `${c.code} — ${localized(c.title, i18n.language)}` })),
            ]}
            onChange={(e) => onChange({ ...block, clause: e.target.value })}
          />
        ) : null}
      </div>
      <p className="muted">{t(`contracts.builder.kindHelp.${block.kind}`)}</p>
      {block.kind !== "clause" ? (
        <div className="translated-fields">
          {LANGS.map((l) => (
            <TextAreaField
              key={l}
              label={t(block.kind === "list" ? `contracts.builder.intro_${l}` : `contracts.builder.text_${l}`)}
              rows={block.kind === "title" || block.kind === "heading" ? 2 : 4}
              lang={l}
              value={text[l] ?? ""}
              onChange={(e) => onChange({ ...block, text: { ...text, [l]: e.target.value } })}
            />
          ))}
        </div>
      ) : null}
      {block.kind === "list" ? (
        <div className="stack">
          <strong>{t("contracts.builder.items")}</strong>
          <ol className="plain-list stack">
            {items.map((item, n) => (
              <li key={n} className="contract-item">
                <div className="translated-fields">
                  {LANGS.map((l) => (
                    <TextAreaField
                      key={l}
                      label={t(`contracts.builder.item_${l}`, { n: n + 1 })}
                      rows={2}
                      lang={l}
                      value={item[l] ?? ""}
                      onChange={(e) => setItems(items.map((it, k) => (k === n ? { ...it, [l]: e.target.value } : it)))}
                    />
                  ))}
                </div>
                <Button variant="ghost" aria-label={t("common.remove")} title={t("common.remove")} onClick={() => setItems(items.filter((_, k) => k !== n))}>
                  <Trash2 size={16} aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ol>
          <div className="row">
            <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setItems([...items, { fr: "", en: "" }])}>
              {t("contracts.builder.addItem")}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function TemplateBuilder({ template, clauses, onClose }: { template: ContractTemplate; clauses: Clause[]; onClose: () => void }) {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshContracts();
  const save = useContractsTemplatesPartialUpdate({ mutation: { onSuccess: refresh } });
  const [name, setName] = useState<Texts>(asTexts(template.name));
  const [status, setStatus] = useState<ContractTemplateStatusEnum>(template.status ?? "draft");
  const [blocks, setBlocks] = useState<Block[]>(template.blocks);
  const [newKind, setNewKind] = useState<BlockKindEnum>("paragraph");
  const [dirty, setDirty] = useState(false);
  const errors = fieldErrors(save.error);

  const update = (next: Block[]) => {
    setBlocks(next);
    setDirty(true);
  };
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    update(next);
  };
  const add = () => {
    const block: Block = { id: newBlockId(newKind), kind: newKind, when: "", text: { fr: "", en: "" } };
    if (newKind === "list") block.items = [{ fr: "", en: "" }];
    if (newKind === "clause") {
      block.clause = clauses[0]?.code ?? "";
      delete block.text;
    }
    update([...blocks, block]);
  };

  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{localized(name, "fr") || template.code}</h2>
          <p className="muted">
            <span className="code-chip">{template.code}</span> · {t("contracts.templates.version", { version: template.version })}
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
      <p className="muted">{t("contracts.templates.frozenNote")}</p>
      <fieldset className="plain-fieldset stack" disabled={!canEdit}>
        <div className="form-grid">
          {LANGS.map((l) => (
            <TextField
              key={l}
              label={t(`contracts.templates.name_${l}`)}
              lang={l}
              value={name[l] ?? ""}
              error={l === "fr" ? errors.name : undefined}
              onChange={(e) => {
                setName((n) => ({ ...n, [l]: e.target.value }));
                setDirty(true);
              }}
            />
          ))}
          <SelectField
            label={t("contracts.templates.status")}
            hint={t("contracts.templates.statusHint")}
            value={status}
            options={STATUSES.map((s) => ({ value: s, label: t(`contracts.templates.statuses.${s}`) }))}
            onChange={(e) => {
              setStatus(e.target.value as ContractTemplateStatusEnum);
              setDirty(true);
            }}
          />
        </div>
        <div className="contract-builder">
          <div className="stack">
            <h3>{t("contracts.builder.title", { count: blocks.length })}</h3>
            {blocks.length ? (
              <ol className="plain-list stack">
                {blocks.map((block, index) => (
                  <BlockEditor
                    key={block.id}
                    block={block}
                    index={index}
                    count={blocks.length}
                    clauses={clauses}
                    onChange={(next) => update(blocks.map((b, k) => (k === index ? next : b)))}
                    onMove={(delta) => move(index, delta)}
                    onDelete={() => update(blocks.filter((_, k) => k !== index))}
                  />
                ))}
              </ol>
            ) : (
              <p className="muted">{t("contracts.builder.empty")}</p>
            )}
            <div className="row contract-code">
              <SelectField
                label={t("contracts.builder.newKind")}
                value={newKind}
                options={KINDS.map((k) => ({ value: k, label: t(`contracts.builder.kinds.${k}`) }))}
                onChange={(e) => setNewKind(e.target.value as BlockKindEnum)}
              />
              <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={add}>
                {t("contracts.builder.addBlock")}
              </Button>
            </div>
          </div>
          <VariablesHelp />
        </div>
        <div className="row sticky-actions">
          <Button
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate(
                { id: template.id, data: { name, status, blocks: blocks.map((b) => ({ ...b, when: b.when ?? "" })) } },
                { onSuccess: () => setDirty(false) },
              )
            }
          >
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
          {dirty ? <span className="muted">{t("contracts.parties.unsaved")}</span> : null}
          {save.isSuccess && !dirty ? (
            <span role="status" className="ok">
              {t("common.saved")}
            </span>
          ) : null}
          {save.isError ? (
            <span role="alert" className="ko">
              {errorText(save.error, t("common.error"))}
            </span>
          ) : null}
        </div>
      </fieldset>
    </GlassCard>
  );
}

function NewTemplateSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const create = useContractsTemplatesCreate();
  const [code, setCode] = useState("");
  const [fr, setFr] = useState("");
  const [en, setEn] = useState("");
  const errors = fieldErrors(create.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        data: {
          code,
          name: { fr, en },
          status: "draft",
          blocks: [
            { id: "title", kind: "title", when: "", text: { fr: "", en: "" } },
            { id: "parties", kind: "parties", when: "", text: { fr: "", en: "" } },
            { id: "services", kind: "services", when: "", text: { fr: "", en: "" } },
            { id: "schedule", kind: "schedule", when: "", text: { fr: "", en: "" } },
            { id: "signatures", kind: "signatures", when: "" },
          ],
        },
      },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          refresh();
          onCreated(response.data.id);
          setCode("");
          setFr("");
          setEn("");
          onClose();
        },
      },
    );
  };
  return (
    <GlassSheet open={open} title={t("contracts.templates.new")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <TextField
          label={t("contracts.templates.code")}
          hint={t("contracts.templates.codeHint")}
          value={code}
          error={errors.code}
          onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-"))}
        />
        <TextField label={t("contracts.templates.name_fr")} lang="fr" value={fr} error={errors.name} onChange={(e) => setFr(e.target.value)} />
        <TextField label={t("contracts.templates.name_en")} lang="en" value={en} onChange={(e) => setEn(e.target.value)} />
        {create.isError && !errors.code && !errors.name ? (
          <p role="alert" className="ko">
            {errorText(create.error, t("common.error"))}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={!code || !fr.trim() || create.isPending}>
            {t("contracts.templates.create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

function ClauseEditor({ clause, onDone }: { clause: Clause | null; onDone: () => void }) {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshContracts();
  const update = useContractsClausesPartialUpdate({ mutation: { onSuccess: refresh } });
  const create = useContractsClausesCreate({ mutation: { onSuccess: refresh } });
  const [code, setCode] = useState(clause?.code ?? "");
  const [title, setTitle] = useState<Texts>(asTexts(clause?.title));
  const [text, setText] = useState<Texts>(asTexts(clause?.text));
  const [review, setReview] = useState(clause?.needs_legal_review ?? true);
  const mutation = clause ? update : create;
  const errors = fieldErrors(mutation.error);
  const onSave = () => {
    if (clause) update.mutate({ id: clause.id, data: { title, text, needs_legal_review: review } }, { onSuccess: onDone });
    else create.mutate({ data: { code, title, text, needs_legal_review: review } }, { onSuccess: onDone });
  };
  return (
    <fieldset className="plain-fieldset stack" disabled={!canEdit}>
      {clause ? null : (
        <TextField
          label={t("contracts.clauses.code")}
          hint={t("contracts.clauses.codeHint")}
          value={code}
          error={errors.code}
          onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-"))}
        />
      )}
      <div className="translated-fields">
        {LANGS.map((l) => (
          <TextField
            key={l}
            label={t(`contracts.clauses.title_${l}`)}
            lang={l}
            value={title[l] ?? ""}
            error={l === "fr" ? errors.title : undefined}
            onChange={(e) => setTitle((v) => ({ ...v, [l]: e.target.value }))}
          />
        ))}
      </div>
      <div className="translated-fields">
        {LANGS.map((l) => (
          <TextAreaField
            key={l}
            label={t(`contracts.clauses.text_${l}`)}
            lang={l}
            rows={8}
            value={text[l] ?? ""}
            error={l === "fr" ? errors.text : undefined}
            onChange={(e) => setText((v) => ({ ...v, [l]: e.target.value }))}
          />
        ))}
      </div>
      <SoftToggle label={t("contracts.clauses.needsReview")} checked={review} onChange={setReview} />
      <div className="row">
        <Button disabled={mutation.isPending || (!clause && !code) || !title.fr?.trim() || !text.fr?.trim()} onClick={onSave}>
          {mutation.isPending ? t("common.loading") : clause ? t("common.save") : t("contracts.clauses.create")}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t("common.close")}
        </Button>
        {mutation.isError && !errors.code && !errors.title && !errors.text ? (
          <span role="alert" className="ko">
            {errorText(mutation.error, t("common.error"))}
          </span>
        ) : null}
      </div>
    </fieldset>
  );
}

function ClauseLibrary({ clauses, loading }: { clauses: Clause[]; loading: boolean }) {
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("contracts.clauses.title")}</h2>
          <p className="muted">{t("contracts.clauses.intro")}</p>
        </div>
        {canEdit ? (
          <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setOpen("new")}>
            {t("contracts.clauses.new")}
          </Button>
        ) : null}
      </div>
      {loading ? <Skeleton style={{ height: 120 }} /> : null}
      {open === "new" ? (
        <div className="installment-row">
          <ClauseEditor clause={null} onDone={() => setOpen(null)} />
        </div>
      ) : null}
      <ul className="plain-list stack">
        {clauses.map((clause) => (
          <li key={clause.id} className="installment-row stack">
            <div className="row between">
              <div className="row">
                <span className="code-chip">{clause.code}</span>
                <strong>{localized(clause.title, i18n.language)}</strong>
                {clause.needs_legal_review ? <Badge tone="offer">{t("contracts.clauses.reviewBadge")}</Badge> : <Badge tone="success">{t("contracts.clauses.reviewed")}</Badge>}
              </div>
              <Button variant="ghost" onClick={() => setOpen(open === clause.id ? null : clause.id)}>
                {open === clause.id ? t("common.close") : canEdit ? t("contracts.clauses.edit") : t("contracts.clauses.read")}
              </Button>
            </div>
            {open === clause.id ? (
              <ClauseEditor key={clause.id} clause={clause} onDone={() => setOpen(null)} />
            ) : (
              <p className="muted contract-clamp" lang={i18n.language}>
                {localized(clause.text, i18n.language)}
              </p>
            )}
          </li>
        ))}
      </ul>
      {!loading && clauses.length === 0 ? <p className="muted">{t("contracts.clauses.empty")}</p> : null}
    </GlassCard>
  );
}

export function ContractTemplatesPage() {
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  const templates = useContractsTemplatesList();
  const clauses = useContractsClausesList();
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const list = templates.data?.status === 200 ? templates.data.data.results : [];
  const clauseList = clauses.data?.status === 200 ? clauses.data.data.results : [];
  const current = list.find((tpl) => tpl.id === selected);

  return (
    <SettingsLayout title={t("contracts.templates.title")} intro={t("contracts.templates.intro")}>
      <GlassCard as="section" className="stack">
        <div className="section-title">
          <h2>{t("contracts.templates.list")}</h2>
          {canEdit ? (
            <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setCreating(true)}>
              {t("contracts.templates.new")}
            </Button>
          ) : null}
        </div>
        {templates.isPending ? <Skeleton style={{ height: 120 }} /> : null}
        {list.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("contracts.templates.code")}</th>
                  <th>{t("contracts.templates.name")}</th>
                  <th>{t("contracts.templates.blocks")}</th>
                  <th>{t("contracts.templates.status")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((tpl) => (
                  <tr key={tpl.id}>
                    <td>
                      <span className="code-chip">{tpl.code}</span>
                    </td>
                    <td>
                      {localized(tpl.name, i18n.language)}
                      <span className="muted"> · v{tpl.version}</span>
                    </td>
                    <td>{tpl.blocks.length}</td>
                    <td>
                      <Badge tone={STATUS_TONE[tpl.status ?? "draft"]}>{t(`contracts.templates.statuses.${tpl.status ?? "draft"}`)}</Badge>
                    </td>
                    <td className="nowrap">
                      <Button variant={selected === tpl.id ? "primary" : "secondary"} onClick={() => setSelected(selected === tpl.id ? null : tpl.id)}>
                        {canEdit ? t("contracts.templates.edit") : t("contracts.templates.read")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !templates.isPending ? (
          <p className="muted">{t("contracts.templates.empty")}</p>
        ) : null}
      </GlassCard>
      {current ? <TemplateBuilder key={current.id} template={current} clauses={clauseList} onClose={() => setSelected(null)} /> : null}
      <ClauseLibrary clauses={clauseList} loading={clauses.isPending} />
      <NewTemplateSheet open={creating} onClose={() => setCreating(false)} onCreated={setSelected} />
    </SettingsLayout>
  );
}
